import { type CatalogCategory, type CatalogProduct } from './firebase'
import { shopData, type Category, type Product } from '../components/pages/site/shopData'

export interface StorefrontProduct extends Product {
  categories: Category[]
}

const SHOP_CATEGORIES: Category[] = [
  'Protein',
  'Strength',
  'Endurance',
  'Health',
  'Stress',
  'Sleep',
]

function isShopCategory(category: CatalogCategory): category is Category {
  return SHOP_CATEGORIES.includes(category as Category)
}

function getCategories(
  catalogProduct: CatalogProduct | undefined,
  fallbackCategory?: Category,
): Category[] {
  const selected = (catalogProduct?.categories ?? []).filter(isShopCategory)
  if (selected.length > 0) return selected
  return fallbackCategory ? [fallbackCategory] : []
}

function getFallbackProduct(catalogProduct: CatalogProduct): StorefrontProduct | null {
  const categories = getCategories(catalogProduct)
  if (!categories.length || catalogProduct.fulfillmentType !== 'physical') {
    return null
  }

return {
  id: catalogProduct.id,
  name: catalogProduct.name,
  tagline: 'A science-backed addition to your wellness routine.',
  category: categories[0],
  categories,
  dose: '—',
  doseUnit: '',
  whyEffective:
    'Product information will be available soon. Please check back for full details.',
  shortDescription:
    'A carefully selected product from Structured Nutrition.',
  status: 'available',
  bottleColor: 'charcoal',
  accentColor: 'tealslate',

  image: catalogProduct.image,
  price: catalogProduct.price,

  lookupKey: catalogProduct.lookupKey,
  stock: catalogProduct.stock,
}
}

/**
 * Merges the public catalog controls into the website's established supplement
 * content. Firestore drives visibility, categories, name, image, and fallback
 * price; static records continue to supply the detailed product content and
 * Stripe purchase references that already exist on the storefront.
 */
export function getStorefrontProducts(
  catalogProducts: CatalogProduct[] | null,
): StorefrontProduct[] {
  const catalogById = new Map(
    (catalogProducts ?? []).map((product) => [product.id, product]),
  )

  const seededProducts = shopData.flatMap((product) => {
const catalogProduct = catalogById.get(product.id)

// If the product isn't in the public catalog, don't show it.
if (!catalogProduct) return []

if (!catalogProduct.visible) return []

    const categories = getCategories(catalogProduct, product.category)
    if (!categories.length) return []

    return [
      {
        ...product,
        name: catalogProduct?.name ?? product.name,
        image: catalogProduct?.image ?? product.image,
        price: catalogProduct?.price ?? product.price,
        category: categories[0],
        categories,
      },
    ]
  })

  const knownIds = new Set(shopData.map((product) => product.id))
  const newProducts = (catalogProducts ?? []).flatMap((catalogProduct) => {
    if (knownIds.has(catalogProduct.id) || !catalogProduct.visible) return []
    const product = getFallbackProduct(catalogProduct)
    return product ? [product] : []
  })

  return [...seededProducts, ...newProducts]
}

export { SHOP_CATEGORIES }
